import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { BigNumber } from "shared/cubiq-model-multi-asset";
import { RateSpinnerComponent } from "shared/base-components/rate-spinner.component";
import { CcyPairPromiseLookupService } from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxFieldNumberDirective } from "../../../../shared/base-components/bfx-field-number";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";
import {
  NUMBER_SPINNER_INPUTS,
  NUMBER_SPINNER_VIEW_PROVIDERS,
} from "../../../../shared/base-components/number-spinner.component";

const ZERO = new CubiqTS.BigNumber(0);

export const SPINNER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxTrailingTriggerSpinnerComponent),
  multi: true,
};

@Component({
  selector: "bfx-trailing-trigger-spinner",
  templateUrl: "../../../../shared/base-components/html/number-spinner.html",
  providers: [SPINNER_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxTrailingTriggerSpinnerComponent extends RateSpinnerComponent implements OnChanges {
  /**********************************************************/
  /* Input parameter config */
  @Input() hideValidation: boolean = false;
  _fixedCcy: string;
  trailingTriggerCalcRequested: boolean;

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldNumberDirective,
    currencyPairService: CcyPairPromiseLookupService,
  ) {
    super(injector, inputDirective, fieldDirective, currencyPairService);
  }

  @Input() set inverted(value: boolean) {
    this.isInverted = value;
  }

  get inverted(): boolean {
    return this.isInverted;
  }

  loadValue(value: BigNumber, considerFocus: boolean = false): void {
    //
    // https://www.bcdfx.com/ijira/browse/OMS-35071 Bugfix: Market entry: Market order cannot be submitted when the currency pair is changed with a new one
    // request price spinner update to avoid setting value while the user is editing

    // added to avoid setting request price based on rate (from the panel) while the user is editing
    if (considerFocus && this.componentFocused) return;
    super.loadValue(value);
  }

  private _trailingPips: CubiqTS.BigNumber;
  private _trackingRate: CubiqTS.BigNumber;
  private _requestPrice: BigNumber;
  private _clientIntent: CubiqTS.OrderIntent;

  public get trailingPips(): CubiqTS.BigNumber {
    return this._trailingPips;
  }

  @Input()
  public set trailingPips(value: CubiqTS.BigNumber) {
    this._trailingPips = value;
    this.requestTrailingTriggerCalc();
  }

  public get trackingRate(): CubiqTS.BigNumber {
    return this._trackingRate;
  }

  @Input()
  public set trackingRate(value: CubiqTS.BigNumber) {
    this._trackingRate = value;
    this.requestTrailingTriggerCalc();
  }

  public get requestPrice(): CubiqTS.BigNumber {
    return this._requestPrice;
  }

  @Input()
  public set requestPrice(value: CubiqTS.BigNumber) {
    this._requestPrice = value;
    this.requestTrailingTriggerCalc();
  }

  public get clientIntent(): CubiqTS.OrderIntent {
    return this._clientIntent;
  }

  @Input()
  public set clientIntent(value: CubiqTS.OrderIntent) {
    this._clientIntent = value;
    this.requestTrailingTriggerCalc();
  }

  public get fixedCcy(): string {
    return this._fixedCcy;
  }

  @Input()
  public set fixedCcy(value: string | { ccy: string; ccyPair: string }) {
    console.log(value);
    this._fixedCcy =
      value != null && typeof value === "object" && Object.hasOwn(value, "ccy")
        ? value.ccy
        : typeof value === "string"
          ? value
          : null;
    this.requestTrailingTriggerCalc();
  }

  public requestTrailingTriggerCalc() {
    this.trailingTriggerCalcRequested = true;
    this.cd.markForCheck();
  }

  public doTrailingTriggerCalc() {
    let value: CubiqTS.BigNumber = null;
    let price = this.trackingRate ?? this.requestPrice;
    this.trailingTriggerCalcRequested = false;

    // explicit trailing trigger is set. don't change anything
    if (!this.disabled) return;
    // no price. don't change anything
    if (!price) return;

    if (typeof this.productCode === "string" && typeof this.fixedCcy === "string") {
      let isBase = this.productCode.startsWith(this.fixedCcy);
      let isBuy = CubiqTS.OrderIntent.BUY === this.clientIntent;

      if (price == null) {
        return;
      }

      if (
        ZERO.isEqualTo(this.trackingRate) &&
        this.requestPrice != null &&
        this.requestPrice instanceof CubiqTS.BigNumber
      ) {
        // prefer request price if trackingRate of zero has been loaded
        price = this.requestPrice;
      }

      let trailing =
        this.step instanceof CubiqTS.BigNumber && this.trailingPips instanceof CubiqTS.BigNumber
          ? this.step.times(this.trailingPips)
          : new CubiqTS.BigNumber("0");

      value =
        (isBase && isBuy) || (!isBase && !isBuy)
          ? price.minus(trailing)
          : (value = price.plus(trailing));
      if (value.isLessThanOrEqualTo(this.min)) {
        value = new CubiqTS.BigNumber(this.min);
      }
    }

    // FXBFXOM-9778 clear old value to allow load to notify
    this.data.value = new BigNumber(NaN);

    this.loadValue(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.trailingTriggerCalcRequested) {
      this.doTrailingTriggerCalc();
    }
    super.ngOnChanges(changes);
  }
}
